import { Controller } from '@hotwired/stimulus';
import {
  currentScopeElementFor,
  diffusionGuardOwnsFocus,
  enqueueSave,
  fieldHasPendingSave,
  fieldKeyFor,
  nextSaveToken,
  observeScopeActivity,
  protocolStateFor,
  queryScope,
  scopeActivityRevision,
  scopeContextFor,
  scopeLastPointerdown,
  waitForScopeSelector,
} from './cr_inline_field_protocol';

// Per-field status regions are ERROR-ONLY (#612): normal save progress and
// success are announced once, globally, by the floating `cr-save-indicator`
// pill. The `saving`/`saved` messages are intentionally empty so those states
// never write visible text (killing the 16 px layout shift), while their
// `data-*-state` keys, `className`, and `aria-busy` handling stay intact for
// tests and for the diffusion guard. Only `error` still speaks.
const SAVE_STATE_CONFIG = {
  dirty: { message: '', className: 'text-neutral-500' },
  saving: { message: '', className: 'text-neutral-500' },
  saved: { message: '', className: 'text-success-700' },
  error: { message: 'Enregistrement impossible. Réessayez.', className: 'text-danger-700' },
};

const STATUS_CLASSES = Object.values(SAVE_STATE_CONFIG).map(({ className }) => className);
const PROTOCOL_DISABLED_ATTRIBUTE = 'data-cr-inline-field-protocol-disabled';

// Upper bound for treating a `relatedTarget: null` blur as an away-click: the
// pointerdown that dismisses a field fires SYNCHRONOUSLY (same gesture) right
// before that blur, so a fresh pointerdown is ~0 ms old. The window is a
// generous safety bound, not a tuning knob (#619).
const AWAY_POINTERDOWN_WINDOW_MS = 500;

// A `Form::SearchableSelectComponent` saved through this protocol (CR remark sheet
// « Statut », plan 2026-10-11 C3 / C3 bis): its value lives in a HIDDEN input that
// never takes focus, the visible control is the list's trigger button.
const SEARCHABLE_SELECT_ROOT = '[data-controller~="searchable-select"]';
const SEARCHABLE_SELECT_HIDDEN =
  'input[type="hidden"][data-searchable-select-target~="hiddenInput"]';
const SEARCHABLE_SELECT_TRIGGER = '[data-searchable-select-target~="trigger"]';

/**
 * CR Inline Field Controller
 *
 * Performs generic single-field PATCHes for editable Compte rendu sections. Each
 * control contributes its Rails field name, endpoint URL, and optional focus/status
 * selectors. Turbo Streams remain server-rendered; this controller adds transport,
 * accessible save state, focus continuity, and the shared diffusion protocol.
 *
 * Logical identity and concurrency:
 * - A field key is always `name@url`; all options in one radio group are one field.
 * - Saves register pending immediately and execute serially per report+field across
 *   every controller instance, including Turbo-replaced hosts.
 * - Every token emits exactly one terminal event after cleanup.
 *
 * Focus behavior (#612):
 * - Automatic post-stream focus is eligible only when the triggering field owned
 *   focus, the stream actually re-rendered a node, and no later pointer, key, focus,
 *   modal, or diffusion activity occurred.
 * - A save queued from a blur that DELIBERATELY moved focus off the field never
 *   re-grabs focus. Two shapes: focus moved to another control (`relatedTarget` an
 *   Element outside the group — Tab/click), OR an away-click into empty page space
 *   (`relatedTarget` null WITH a real pointerdown outside the group in the same gesture
 *   that produced the blur). The activity epoch alone cannot catch the away-click: its
 *   `pointerdown` bumps the epoch BEFORE the blur that snapshots it, so the epoch is
 *   already advanced by the time patchField reads it. A bare `relatedTarget` null is NOT
 *   enough — a Turbo replacement, a node removal, or a programmatic `.blur()` surfaces
 *   the SAME null with no away-pointerdown, and those MUST keep restoration alive (#619).
 *   Restoration still fires when focus is back on the field's live control group at
 *   render time — the legitimate "still editing / just re-focused while Turbo swapped
 *   the node" case this restoration exists for.
 * - Non-blur saves (a select/radio/checkbox `change` keeps focus on the control) stay
 *   eligible so the control keeps focus across its Turbo re-render.
 * - The optional focus selector is preferred; the stable field selector is fallback.
 * - Turbo replacements are located with a bounded MutationObserver wait.
 * - A `Form::SearchableSelectComponent` value (hidden input, `change` bubbling to an
 *   ancestor action) is focused through its trigger: the trigger counts as the
 *   field's control for focus ownership and receives focus after the replace (C3).
 *   Its committed value is snapshotted on the first gesture inside the list and,
 *   on a failure without a stream, restored and repainted silently through
 *   `searchable-select#writeValueSilently` (no `change`, so no second PATCH; C3 bis).
 *
 * Values:
 * - url: Fallback PATCH URL when a control has no action URL parameter.
 * - csrfToken: Optional CSRF token; defaults to Rails' meta tag.
 *
 * Actions:
 * - patchField: Queues a PATCH containing the triggering control's current value.
 * - commitOnEnter: Bound to `keydown.enter` (and `keydown.shift+enter`) on a one-line
 *   <textarea>: blocks the line break and leaves the field, so the save goes through
 *   the field's own `blur->…#patchField` (one write path, one PATCH).
 *
 * Events dispatched on `window` by the shared protocol:
 * - cr-inline-field:pending: Save token registered before queue/network activity.
 * - cr-inline-field:succeeded: Successful 2xx Turbo Stream reached terminal state.
 * - cr-inline-field:failed: Validation, transport, renderer, or response failure.
 *
 * Event detail:
 * - token: Browser-unique save identifier.
 * - scopeKey: Explicit `meeting-report-<id>` report identity.
 * - fieldKey: Logical `name@url` identity.
 * - field/fieldSelector/focusSelector: Original and replacement focus metadata.
 * - outcome: `pending`, `succeeded`, or `failed`.
 *
 * @example
 * <div data-cr-inline-field-scope="meeting-report-42">
 *   <div data-controller="cr-inline-field">
 *     <textarea name="agenda[label]"
 *               data-action="blur->cr-inline-field#patchField"
 *               data-cr-inline-field-url-param="/projects/1/reports/42/agenda/3"
 *               data-cr-inline-field-status-param="#agenda-status"></textarea>
 *     <span id="agenda-status" role="status" aria-live="polite"></span>
 *   </div>
 * </div>
 */
export default class extends Controller {
  static values = {
    url: String,
    csrfToken: String,
  };

  /**
   * Captures committed snapshots, observes user activity on the shared report
   * scope, and reapplies disabled state when this host reconnects mid-save.
   */
  connect() {
    const scope = scopeContextFor(this.element);
    this.scopeKey = scope.scopeKey;
    this.scopeElement = scope.scopeElement;
    this.protocol = protocolStateFor(this.scopeKey);
    observeScopeActivity(this.scopeKey, this.scopeElement);

    this.committedSnapshots = new WeakMap();
    this.lastFocusedField = null;
    this.captureCommittedValueHandler = this.#captureCommittedValue.bind(this);
    this.markDirtyHandler = this.#markDirty.bind(this);

    this.element.addEventListener('pointerdown', this.captureCommittedValueHandler, true);
    this.element.addEventListener('focusin', this.captureCommittedValueHandler, true);
    this.element.addEventListener('input', this.markDirtyHandler, true);
    this.element.addEventListener('change', this.markDirtyHandler, true);

    this.#synchronizePendingFields();
  }

  /**
   * Removes only listeners owned by this controller instance. Module-scoped save
   * queues and tokens continue through Turbo disconnects until terminal state.
   */
  disconnect() {
    this.element.removeEventListener('pointerdown', this.captureCommittedValueHandler, true);
    this.element.removeEventListener('focusin', this.captureCommittedValueHandler, true);
    this.element.removeEventListener('input', this.markDirtyHandler, true);
    this.element.removeEventListener('change', this.markDirtyHandler, true);
  }

  /**
   * Registers and serializes one field PATCH. Values and rollback snapshots are
   * captured before queueing so a later Turbo replacement cannot change the write.
   *
   * @param {Event} event Stimulus action event with optional url/focus/status params.
   * @returns {Promise<void>}
   */
  async patchField(event) {
    const field = this.#formControl(event.target);
    if (!field || !field.name) return;

    const url = event.params?.url || this.#urlFor(field);
    if (!url) return;

    // Removal-blur discriminator: a Turbo turbo_stream.replace that removes the
    // focused input makes Chromium fire a GENUINE `blur` on the detaching element
    // (Stimulus unbinds asynchronously). At dispatch time the element is still
    // `isConnected === true`; one microtask later it is `false` for a removal-blur
    // and stays `true` for a real user blur. Yielding one microtask, then bailing
    // when disconnected, drops the phantom save whose value is already stale —
    // before any state flip or token consumption — so it can't self-cascade.
    //
    // The same phantom arrives as a `change` (ANOM-1): the date-picker's display input
    // is the focused node, and its removal-blur makes `date-picker#onBlur` rewrite the
    // OUTGOING hidden input and emit `change` on it while it is still attached. Each
    // reply replaced the block again and Turbo refocused the display by id, so the
    // PATCH looped forever. A field detached one microtask after ANY event is stale.
    await Promise.resolve();
    if (!field.isConnected) return;

    const controls = this.#fieldControls(field.name, url);
    const context = {
      token: nextSaveToken(),
      scopeKey: this.scopeKey,
      ...this.#fieldContext(field, url, event.params || {}),
      value: this.#fieldValue(field),
      ownsFocus: this.#fieldOwnedFocus(event, field, controls),
      dismissedByBlur: this.#fieldDismissedByBlur(event, controls),
      activityRevision: scopeActivityRevision(this.scopeKey),
      controls,
    };
    const priorSnapshot = this.committedSnapshots.get(field) || this.#snapshot(field, url);

    this.#setFieldState(context, 'saving');
    this.#setControlsDisabled(controls, true);

    await enqueueSave(
      context,
      () => this.#performPatch(context, priorSnapshot),
      (result) => this.#finishSave(context, result)
    );
  }

  /**
   * Enter on a one-line <textarea> (CR agenda label, T18/S8): prevents the line
   * break and blurs the control, so the save goes through its existing
   * `blur->cr-inline-field#patchField` action. Saving here directly would queue a
   * second PATCH when the field later loses focus, and would disable a still
   * focused control. Enter that confirms an IME composition is left alone.
   *
   * @param {KeyboardEvent} event Stimulus `keydown.enter` action event.
   */
  commitOnEnter(event) {
    if (event.isComposing) return;

    event.preventDefault();
    this.#formControl(event.target)?.blur();
  }

  /**
   * Executes the network portion of a queued save and renders Turbo Stream bodies.
   * Validation streams are rendered but remain failed. Non-stream/network failures
   * restore the complete prior control snapshot.
   *
   * @param {Object} context
   * @param {Object} priorSnapshot
   * @returns {Promise<{ outcome: 'succeeded'|'failed', rendered: boolean }>}
   */
  async #performPatch(context, priorSnapshot) {
    let outcome = 'failed';
    let rendered = false;

    try {
      const formData = new FormData();
      formData.append(context.field.name, context.value);

      const response = await fetch(context.url, {
        method: 'PATCH',
        body: formData,
        credentials: 'same-origin',
        headers: {
          Accept: 'text/vnd.turbo-stream.html',
          'X-CSRF-Token': this.#csrfToken(),
        },
      });
      const contentType = response.headers.get('Content-Type') || '';
      const turboStream = contentType.includes('text/vnd.turbo-stream.html');

      if (!turboStream) {
        this.#restoreSnapshot(priorSnapshot);
        console.error(`[cr-inline-field] PATCH failed: HTTP ${response.status} (${contentType})`);
        return { outcome, rendered };
      }

      const renderStreamMessage = window.Turbo?.renderStreamMessage;
      if (typeof renderStreamMessage !== 'function') {
        throw new Error('Turbo Stream renderer is unavailable');
      }

      const body = await response.text();
      renderStreamMessage(body);
      // RES-2 (NextMeetingController) replies 200 text/vnd.turbo-stream.html with an
      // EMPTY body: nothing re-rendered, so there is no replacement node to restore
      // focus to. Treating an empty stream as "not rendered" skips the focus pass
      // entirely, so the bounded replacement wait never fires against an unchanged DOM
      // (#612 perceived 750 ms latency on « Prochaine réunion »). The save still
      // settles as succeeded — `outcome` is derived from the status, not from `rendered`.
      rendered = body.trim().length > 0;
      outcome = response.ok ? 'succeeded' : 'failed';
    } catch (error) {
      this.#restoreSnapshot(priorSnapshot);
      console.error('[cr-inline-field] PATCH failed:', error);
    }

    return { outcome, rendered };
  }

  /**
   * Completes accessible UI state before the shared protocol emits the terminal
   * event. The final token for a field re-enables the whole current control group.
   *
   * @param {Object} context
   * @param {{ outcome: 'succeeded'|'failed' }} result
   */
  async #finishSave(context, result) {
    if (!fieldHasPendingSave(context.scopeKey, context.fieldKey)) {
      this.#setControlsDisabled(
        [
          ...new Set([
            ...context.controls,
            ...this.#fieldControls(context.field.name, context.url),
          ]),
        ],
        false
      );
    }

    const terminalState = result.outcome === 'succeeded' ? 'saved' : 'error';

    if (context.field.isConnected) {
      this.#setFieldState(context, terminalState);
    }

    if (result.outcome === 'succeeded') {
      for (const control of this.#fieldControls(context.field.name, context.url)) {
        this.committedSnapshots.set(control, this.#snapshot(control, context.url));
      }
    }

    if (result.rendered) {
      // Capture the OUTGOING status node before Turbo swaps it (the stream mutation
      // is applied asynchronously, so it is still connected at this synchronous
      // point). The in-place `#setFieldState` above landed on that outgoing node
      // (or on nothing, when the whole row/block replace already detached it), so
      // the terminal state must be reapplied to the freshly rendered region.
      const outgoingStatus = queryScope(this.#currentScopeElement(), context.statusSelector);
      await this.#restoreFocusAfterRender(context);
      await this.#restoreStatusAfterRender(context, terminalState, outgoingStatus);
    }
  }

  /**
   * Captures the full radio group (or scalar control) before browser mutation.
   * Pointer capture covers clicking a visually wrapped radio label; focus capture
   * covers keyboard changes and ordinary text fields.
   *
   * @param {Event} event
   */
  #captureCommittedValue(event) {
    this.#captureSearchableSelectValue(event.target);

    const field = this.#patchableControl(event.target);
    if (!field) return;

    const url = this.#urlFor(field);
    if (!url) return;

    const snapshot = this.#snapshot(field, url);
    for (const control of this.#fieldControls(field.name, url)) {
      this.committedSnapshots.set(control, snapshot);
    }

    if (event.type === 'focusin') this.lastFocusedField = field;
  }

  /**
   * Snapshots a searchable-select's hidden value the FIRST time a gesture lands
   * inside the list (trigger, label, option), before any choice writes it (C3 bis).
   * The hidden input never receives pointer or focus events itself, and the list
   * refocuses its trigger AFTER writing the new value, so a later capture would
   * snapshot the unsaved choice: an existing snapshot is kept until a successful
   * save (`#finishSave`) replaces it. A Turbo-replaced list is a new element and is
   * captured afresh from its server-rendered value.
   *
   * @param {EventTarget|null} target
   */
  #captureSearchableSelectValue(target) {
    if (!(target instanceof Element)) return;

    const root = target.closest(SEARCHABLE_SELECT_ROOT);
    if (!root || !this.element.contains(root)) return;

    const field = this.#patchableControl(root.querySelector(SEARCHABLE_SELECT_HIDDEN));
    if (!field || this.committedSnapshots.has(field)) return;

    const url = this.#urlFor(field);
    if (!url) return;

    this.committedSnapshots.set(field, this.#snapshot(field, url));
  }

  /**
   * Returns the searchable-select root owning a hidden value input, or null for
   * every other control (radios, text inputs, textareas, other hidden inputs).
   *
   * @param {Element|null} field
   * @returns {Element|null}
   */
  #searchableSelectRoot(field) {
    if (!(field instanceof HTMLInputElement) || !field.matches(SEARCHABLE_SELECT_HIDDEN)) {
      return null;
    }

    return field.closest(SEARCHABLE_SELECT_ROOT);
  }

  /**
   * The element that holds focus on behalf of a field that cannot: the trigger of
   * the searchable-select owning a hidden value input (C3). Null for any other field.
   *
   * @param {Element|null} field
   * @returns {HTMLElement|null}
   */
  #focusProxyFor(field) {
    return this.#searchableSelectRoot(field)?.querySelector(SEARCHABLE_SELECT_TRIGGER) || null;
  }

  /**
   * Repaints a searchable-select (label, dot, aria-selected, checkmarks) to a
   * restored value WITHOUT a `change` event, which would queue a second PATCH
   * (C3 bis). Defensive: no list controller, or an older one, leaves only the hidden
   * input restored.
   *
   * @param {Element} field
   * @param {string} value
   */
  #repaintSearchableSelect(field, value) {
    const root = this.#searchableSelectRoot(field);
    if (!root) return;

    const select = this.application?.getControllerForElementAndIdentifier?.(
      root,
      'searchable-select'
    );
    select?.writeValueSilently?.(value);
  }

  /**
   * Marks a patchable control dirty before its Stimulus action enters saving state.
   *
   * @param {Event} event
   */
  #markDirty(event) {
    const field = this.#patchableControl(event.target);
    if (!field) return;

    const url = this.#urlFor(field);
    if (!url) return;

    this.#setFieldState(this.#fieldContext(field, url, this.#actionParams(field)), 'dirty');
  }

  /**
   * The params Stimulus would hand `patchField` for this control: read on the element
   * that CARRIES the `patchField` action, which is the control itself for an ordinary
   * field and an ANCESTOR for a searchable-select's hidden input (F3). Reading the
   * control's own dataset left that field with no status selector, so its `#…-feedback`
   * region was never found outside `patchField` (dirty state, reconnect sync).
   * Unset keys stay undefined: `#fieldContext` then falls back to the control's dataset.
   *
   * @param {HTMLElement} field
   * @returns {Object}
   */
  #actionParams(field) {
    const owner = field.closest('[data-action*="cr-inline-field#patchField"]') || field;
    const { dataset } = owner;

    return {
      url: dataset.crInlineFieldUrlParam,
      queue: dataset.crInlineFieldQueueParam,
      focus: dataset.crInlineFieldFocusParam,
      status: dataset.crInlineFieldStatusParam,
      savedMessage: dataset.crInlineFieldSavedMessageParam,
    };
  }

  /**
   * Returns a supported control only when it belongs to a patchField action in
   * this controller root. Label descendants resolve to their contained input.
   *
   * @param {EventTarget|null} candidate
   * @returns {HTMLInputElement|HTMLSelectElement|HTMLTextAreaElement|null}
   */
  #patchableControl(candidate) {
    let field = this.#formControl(candidate);

    if (!field && candidate instanceof Element) {
      field = this.#formControl(
        candidate.closest('label')?.querySelector('input, select, textarea')
      );
    }

    if (!field) return null;

    const actionOwner = field.closest('[data-action*="cr-inline-field#patchField"]');
    return actionOwner && this.element.contains(actionOwner) ? field : null;
  }

  /**
   * Narrows an event target to controls supported by the inline-save protocol.
   *
   * @param {EventTarget|null} candidate
   * @returns {HTMLInputElement|HTMLSelectElement|HTMLTextAreaElement|null}
   */
  #formControl(candidate) {
    if (
      candidate instanceof HTMLInputElement ||
      candidate instanceof HTMLSelectElement ||
      candidate instanceof HTMLTextAreaElement
    ) {
      return candidate;
    }

    return null;
  }

  /**
   * Resolves a control's action URL from itself, a delegated action owner, or the
   * controller fallback value.
   *
   * @param {HTMLElement} field
   * @returns {string}
   */
  #urlFor(field) {
    return (
      field.dataset.crInlineFieldUrlParam ||
      field.closest('[data-cr-inline-field-url-param]')?.dataset.crInlineFieldUrlParam ||
      this.urlValue ||
      ''
    );
  }

  /**
   * Builds immutable save and focus metadata that remains usable after Turbo
   * detaches the original field.
   *
   * @param {HTMLInputElement|HTMLSelectElement|HTMLTextAreaElement} field
   * @param {string} url
   * @param {Object} params
   * @returns {Object}
   */
  #fieldContext(field, url, params) {
    const fieldSelector = this.#fieldSelector(field, url);

    return {
      field,
      url,
      fieldKey: fieldKeyFor(field.name, url),
      // Optional serialization-queue override (#617 C.1): distinct from fieldKey so
      // several avancement rows sharing one lot header serialize their writes.
      queueKey:
        params.queue || field.dataset.crInlineFieldQueueParam || fieldKeyFor(field.name, url),
      fieldSelector,
      focusSelector: params.focus || field.dataset.crInlineFieldFocusParam || fieldSelector,
      statusSelector: params.status || field.dataset.crInlineFieldStatusParam || null,
      // Optional per-field « saved » copy. Absent → the (empty) global default, so
      // existing error-only surfaces keep announcing nothing on success (#612).
      savedMessage: params.savedMessage || field.dataset.crInlineFieldSavedMessageParam || null,
    };
  }

  /**
   * Returns every control in this report scope that belongs to one logical field.
   * This makes radio snapshot/disable/restore group-wide and crosses Turbo hosts.
   *
   * @param {string} name
   * @param {string} url
   * @returns {Array<HTMLInputElement|HTMLSelectElement|HTMLTextAreaElement>}
   */
  #fieldControls(name, url) {
    return Array.from(
      this.#currentScopeElement().querySelectorAll('input[name], select[name], textarea[name]')
    ).filter((control) => control.name === name && this.#urlFor(control) === url);
  }

  /**
   * Captures either a scalar value, checkbox state, or every checked radio option.
   *
   * @param {HTMLInputElement|HTMLSelectElement|HTMLTextAreaElement} field
   * @param {string} url
   * @returns {Object}
   */
  #snapshot(field, url) {
    if (field instanceof HTMLInputElement && field.type === 'radio') {
      return {
        kind: 'radio',
        entries: this.#fieldControls(field.name, url).map((control) => ({
          control,
          checked: control.checked,
        })),
      };
    }

    if (field instanceof HTMLInputElement && field.type === 'checkbox') {
      return { kind: 'checkbox', field, checked: field.checked };
    }

    return { kind: 'value', field, value: field.value };
  }

  /**
   * Restores the complete pre-save snapshot after a failure without a Turbo Stream.
   * A radio retry becomes actionable because the failed choice is unchecked again.
   *
   * @param {Object} snapshot
   */
  #restoreSnapshot(snapshot) {
    if (snapshot.kind === 'radio') {
      for (const { control, checked } of snapshot.entries) {
        if (control.isConnected) control.checked = checked;
      }
    } else if (snapshot.kind === 'checkbox') {
      if (snapshot.field.isConnected) snapshot.field.checked = snapshot.checked;
    } else if (snapshot.field.isConnected) {
      snapshot.field.value = snapshot.value;
      this.#repaintSearchableSelect(snapshot.field, snapshot.value);
    }
  }

  /**
   * Applies protocol-only disabled state without changing server-disabled controls.
   * Newly connected Turbo replacements are marked and later re-enabled safely.
   *
   * @param {Array<HTMLElement>} controls
   * @param {boolean} disabled
   */
  #setControlsDisabled(controls, disabled) {
    for (const control of controls) {
      if (disabled) {
        if (!control.disabled) {
          control.setAttribute(PROTOCOL_DISABLED_ATTRIBUTE, 'true');
          control.disabled = true;
        }
      } else if (control.hasAttribute(PROTOCOL_DISABLED_ATTRIBUTE)) {
        control.removeAttribute(PROTOCOL_DISABLED_ATTRIBUTE);
        control.disabled = false;
      }
      this.#setFocusProxyBusy(control, disabled);
    }
  }

  /**
   * A searchable-select's hidden value is locked by `disabled` (the list refuses to
   * open while it is, review finding 2), but its TRIGGER is never disabled: it holds
   * focus (C3), and a disabled focused button drops focus to <body>. The lock is
   * announced on the trigger with `aria-disabled`, set and removed by the protocol
   * only (same marker attribute), so a server-rendered `aria-disabled` is never touched.
   *
   * @param {HTMLElement} control
   * @param {boolean} disabled
   */
  #setFocusProxyBusy(control, disabled) {
    const proxy = this.#focusProxyFor(control);
    if (!proxy) return;

    if (disabled) {
      if (!proxy.hasAttribute('aria-disabled')) {
        proxy.setAttribute(PROTOCOL_DISABLED_ATTRIBUTE, 'true');
        proxy.setAttribute('aria-disabled', 'true');
      }
    } else if (proxy.hasAttribute(PROTOCOL_DISABLED_ATTRIBUTE)) {
      proxy.removeAttribute(PROTOCOL_DISABLED_ATTRIBUTE);
      proxy.removeAttribute('aria-disabled');
    }
  }

  /**
   * Reconciles cached or replaced controls with the shared pending/terminal state.
   * A PATCH may settle while this host is disconnected, leaving Turbo's cached
   * copy disabled and visually saving until the controller reconnects.
   */
  #synchronizePendingFields() {
    const controls = Array.from(
      this.element.querySelectorAll('input[name], select[name], textarea[name]')
    );

    for (const field of controls) {
      const patchable = this.#patchableControl(field);
      if (!patchable) continue;

      const url = this.#urlFor(field);
      if (!url) continue;

      const fieldKey = fieldKeyFor(field.name, url);
      const context = this.#fieldContext(field, url, this.#actionParams(field));
      const controlsForField = this.#fieldControls(field.name, url);
      if (fieldHasPendingSave(this.scopeKey, fieldKey)) {
        this.#setControlsDisabled(controlsForField, true);
        this.#setFieldState(context, 'saving');
        continue;
      }

      this.#setControlsDisabled(controlsForField, false);
      const latest = this.protocol.latestByField.get(fieldKey);
      if (!['succeeded', 'failed'].includes(latest?.outcome)) continue;

      this.#setFieldState(context, latest.outcome === 'succeeded' ? 'saved' : 'error');
    }
  }

  /**
   * Determines whether this action began from a control that owned focus. Blur is
   * eligible only when this controller previously observed that field's focus.
   *
   * @param {Event} event
   * @param {HTMLElement} field
   * @param {Array<HTMLElement>} controls
   * @returns {boolean}
   */
  #fieldOwnedFocus(event, field, controls) {
    if (event.type === 'blur') {
      const ownedFocus = this.lastFocusedField === field;
      this.lastFocusedField = null;
      return ownedFocus;
    }

    // A searchable-select's trigger holds focus for its hidden value input (C3).
    const active = document.activeElement;
    return (
      controls.includes(active) ||
      controls.some((control) => this.#focusProxyFor(control) === active)
    );
  }

  /**
   * Records whether the triggering blur moved focus off the field's control group
   * by a DELIBERATE user gesture, so post-render focus must not be re-grabbed (#612).
   * Two disjoint dismissal shapes:
   *
   * (a) `relatedTarget` is an Element OUTSIDE the group — focus moved to another
   *     control (Tab, or a click/programmatic focus onto a focusable element). An
   *     unambiguous deliberate move, independent of any pointer gesture.
   * (b) `relatedTarget` is null — focus went to empty page space. This is ALSO how a
   *     purely programmatic/DOM-driven blur surfaces (a Turbo replacement, a node
   *     removal, or a raw `.blur()`), which is NOT a dismissal and must keep the
   *     legitimate restoration alive (#619 — two system tests broke when `null` alone
   *     was read as a dismiss). It is a real away-click ONLY when a genuine pointerdown
   *     landed outside the group within the same gesture window that produced the blur.
   *
   * Non-blur saves never dismiss: a select/radio/checkbox `change` keeps focus on the
   * control, and a hidden date input fired via `change` is never focused at all.
   *
   * @param {Event} event
   * @param {Array<HTMLElement>} controls
   * @returns {boolean}
   */
  #fieldDismissedByBlur(event, controls) {
    if (event.type !== 'blur') return false;

    const nextFocus = event.relatedTarget;
    if (nextFocus instanceof Element) return !controls.includes(nextFocus);

    return this.#blurFollowedAwayPointerdown(event, controls);
  }

  /**
   * True when the `relatedTarget: null` blur was produced by a real away-click: a
   * genuine pointerdown that landed outside the field's control group within the
   * gesture window immediately preceding the blur. The away-click's pointerdown
   * fires synchronously right before the blur (same mousedown default action), so a
   * legitimate dismiss carries a ~0 ms-old pointerdown OUTSIDE the group. A
   * programmatic `.blur()`, a Turbo replacement, or a node removal carries no such
   * recent-and-outside pointerdown — the only pointerdown on record is the earlier
   * in-group click that focused the field — so restoration correctly resumes.
   *
   * @param {Event} blurEvent
   * @param {Array<HTMLElement>} controls
   * @returns {boolean}
   */
  #blurFollowedAwayPointerdown(blurEvent, controls) {
    const pointer = scopeLastPointerdown(this.scopeKey);
    if (!pointer || !(pointer.target instanceof Element)) return false;

    const sinceGesture = blurEvent.timeStamp - pointer.timeStamp;
    if (!(sinceGesture >= 0 && sinceGesture <= AWAY_POINTERDOWN_WINDOW_MS)) return false;

    return !controls.some(
      (control) => control === pointer.target || control.contains(pointer.target)
    );
  }

  /**
   * Reports whether the user is still away from the field at restore time, so focus
   * must not be re-grabbed. Only a blur-triggered dismissal (see #fieldDismissedByBlur)
   * qualifies, and it is honored UNLESS focus is, at this moment, back on the field's
   * live control group — the legitimate "still editing / just re-focused while Turbo
   * swapped the node" case that this restoration exists for. The group is re-queried
   * against the current scope so a Turbo-replaced control still counts.
   *
   * @param {Object} context
   * @returns {boolean}
   */
  #userDismissedField(context) {
    if (!context.dismissedByBlur) return false;

    const groupControls = this.#fieldControls(context.field.name, context.url);
    return !groupControls.includes(document.activeElement);
  }

  /**
   * After Turbo renders, restores or redirects focus only when the activity epoch
   * still matches the triggering field's snapshot. Preferred focus comes first;
   * the stable field selector is the fallback.
   *
   * @param {Object} context
   * @returns {Promise<void>}
   */
  async #restoreFocusAfterRender(context) {
    if (!context.ownsFocus) return;
    if (this.#userDismissedField(context)) return;
    if (diffusionGuardOwnsFocus(context.scopeKey)) return;
    if (scopeActivityRevision(context.scopeKey) !== context.activityRevision) return;

    const selectors = [context.focusSelector, context.fieldSelector];
    const scopeElement = this.#currentScopeElement();
    const ignoredElements = selectors
      .map((selector) => queryScope(scopeElement, selector))
      .filter(Boolean);
    const target = await waitForScopeSelector(scopeElement, selectors, { ignoredElements });

    if (diffusionGuardOwnsFocus(context.scopeKey)) return;
    if (scopeActivityRevision(context.scopeKey) !== context.activityRevision) return;

    // A hidden searchable-select value hands focus to the REPLACED list's trigger (C3).
    const focusTarget = this.#focusProxyFor(target) || target;
    if (
      !(focusTarget instanceof HTMLElement) ||
      !focusTarget.isConnected ||
      focusTarget.matches(':disabled')
    )
      return;

    focusTarget.focus({ preventScroll: true });
  }

  /**
   * Reapplies the terminal save state to the accessible status region once Turbo
   * has replaced the field's row or block. The region keeps a stable id (derived
   * from the record's dom_id), so it survives the replace even though the ORIGINAL
   * field and its host were detached by it — which is exactly why `#finishSave`'s
   * in-place `#setFieldState` (bound to the outgoing node via `this.element`) leaves
   * the freshly rendered region blank on the five replace-backed fields (#612).
   *
   * The bounded replacement wait is reused (`waitForScopeSelector`, same ~750 ms
   * safety bound as focus restoration) and the OUTGOING node is ignored so the wait
   * resolves the REPLACEMENT, not the node Turbo is about to remove — mirroring
   * `#restoreFocusAfterRender`. This runs UNCONDITIONALLY of focus ownership: a field
   * dismissed by an away-click (type → click outside — the reported repro) still
   * needs its « enregistré » / « erreur » state, only its focus must not be re-grabbed.
   *
   * No-ops preserved: a render that changes or drops the status id yields no match
   * (silent no-op); a field with no replace (empty stream) never reaches this path
   * (`result.rendered` is false), so `#setFieldState` alone handles it. No timer is
   * owned here beyond the wait's own self-clearing bound, so a rapid re-replace
   * cannot leak one.
   *
   * @param {Object} context
   * @param {'saved'|'error'} state
   * @param {Element|null} outgoingStatus Pre-replace status node to exclude from the wait.
   * @returns {Promise<void>}
   */
  async #restoreStatusAfterRender(context, state, outgoingStatus) {
    if (!context.statusSelector) return;

    const scopeElement = this.#currentScopeElement();
    const status = await waitForScopeSelector(scopeElement, [context.statusSelector], {
      ignoredElements: outgoingStatus ? [outgoingStatus] : [],
    });

    if (status instanceof HTMLElement) this.#applyStatusState(status, state, context.savedMessage);
  }

  /**
   * Resolves the report root lazily so an in-flight save can finish against a root
   * installed by a newer controller after a full Turbo replacement.
   *
   * @returns {Element}
   */
  #currentScopeElement() {
    return currentScopeElementFor(this.scopeKey, this.scopeElement);
  }

  /**
   * Creates a stable selector. ID-less controls anchor to the nearest stable row or
   * section ID and include escaped field name plus action URL, avoiding broad IDs.
   *
   * @param {HTMLElement} field
   * @param {string} url
   * @returns {string|null}
   */
  #fieldSelector(field, url) {
    if (field.id) return `#${this.#escapeIdentifier(field.id)}`;

    const container = field.closest('[id]');
    if (!container) return null;

    const nameSelector = `[name="${this.#escapeAttribute(field.name)}"]`;
    const urlSelector = `[data-cr-inline-field-url-param="${this.#escapeAttribute(url)}"]`;
    const actionOwner = field.closest('[data-cr-inline-field-url-param]');
    let relativeSelector = nameSelector;

    if (actionOwner === field) {
      relativeSelector = `${nameSelector}${urlSelector}`;
    } else if (actionOwner && container.contains(actionOwner)) {
      relativeSelector = `${urlSelector} ${nameSelector}`;
    }

    return `#${this.#escapeIdentifier(container.id)} ${relativeSelector}`;
  }

  /**
   * Escapes a CSS identifier with a deterministic fallback.
   *
   * @param {string} value
   * @returns {string}
   */
  #escapeIdentifier(value) {
    if (typeof window.CSS?.escape === 'function') return window.CSS.escape(value);
    return value.replace(/([^\w-])/g, '\\$1');
  }

  /**
   * Escapes a quoted CSS attribute value, including URL punctuation and Rails names.
   *
   * @param {string} value
   * @returns {string}
   */
  #escapeAttribute(value) {
    return String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/\n/g, '\\a ');
  }

  /**
   * Reads a Rails-compatible scalar value from the triggering control.
   *
   * @param {HTMLInputElement|HTMLSelectElement|HTMLTextAreaElement} field
   * @returns {string}
   */
  #fieldValue(field) {
    if (field instanceof HTMLInputElement && field.type === 'checkbox') {
      return field.checked ? '1' : '0';
    }

    return field.value;
  }

  /**
   * Updates the control plus its optional accessible status region.
   *
   * @param {Object} context
   * @param {'dirty'|'saving'|'saved'|'error'} state
   */
  #setFieldState(context, state) {
    if (!SAVE_STATE_CONFIG[state]) return;

    context.field.dataset.crInlineFieldState = state;
    const status = this.#statusElement(context.statusSelector);
    if (!status) return;

    this.#applyStatusState(status, state, context.savedMessage);
  }

  /**
   * Writes one save state onto a resolved accessible status region. Shared by the
   * in-place `#setFieldState` and the post-replace `#restoreStatusAfterRender`, so
   * the message, tokens, and `aria-busy` stay identical whichever node is live.
   *
   * @param {HTMLElement} status
   * @param {'dirty'|'saving'|'saved'|'error'} state
   * @param {string|null} savedMessage Optional per-field « saved » copy override.
   */
  #applyStatusState(status, state, savedMessage = null) {
    const config = SAVE_STATE_CONFIG[state];
    if (!config) return;

    // Only the terminal « saved » state honors a per-field message override so a
    // surface can opt into a visible confirmation (e.g. « Avancement enregistré. »)
    // without reintroducing the global layout-shift the empty default removed (#612).
    const message = state === 'saved' && savedMessage ? savedMessage : config.message;

    status.dataset.crInlineFieldState = state;
    status.dataset.noteState = state;
    status.textContent = message;
    status.classList.remove(...STATUS_CLASSES);
    status.classList.add(config.className);
    status.setAttribute('aria-busy', state === 'saving' ? 'true' : 'false');
  }

  /**
   * Resolves a status selector against the CURRENT report scope, not this
   * controller's own (possibly detached) host. When a Turbo row/block replace lands
   * mid-save the original host is detached, so a `this.element`-scoped lookup would
   * write the terminal « saved »/« error » state onto the outgoing node and leave
   * the freshly rendered region stuck showing « saving » — the #617 stuck-spinner.
   * Scoping to the live report root (which still CONTAINS this host on the common,
   * un-replaced path) makes the state land on the new row while keeping every
   * id-based selector (letterhead, …) resolving to the exact same unique node.
   *
   * @param {string|null} selector
   * @returns {Element|null}
   */
  #statusElement(selector) {
    return queryScope(this.#currentScopeElement(), selector);
  }

  /**
   * Resolves the CSRF token from a Stimulus value or Rails' standard meta tag.
   *
   * @returns {string}
   */
  #csrfToken() {
    if (this.hasCsrfTokenValue && this.csrfTokenValue) return this.csrfTokenValue;
    return document.head.querySelector('meta[name="csrf-token"]')?.content || '';
  }
}
